import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Shield, FileText, Cookie } from 'lucide-react';

export type LegalType = 'privacy' | 'terms' | 'cookies' | null;

interface LegalModalsProps {
  type: LegalType;
  isOpen: boolean;
  onClose: () => void;
}

export function LegalModals({ type, isOpen, onClose }: LegalModalsProps) {
  const content = {
    privacy: {
      title: 'Privacy Policy',
      icon: <Shield className="h-6 w-6 text-brand-green" />,
      sections: [
        {
          heading: 'Information We Collect',
          text: 'We collect personal information such as your name, contact details, and shipping address to process your orders and provide a personalized experience.'
        },
        {
          heading: 'How We Use Your Data',
          text: 'Your data is used for order fulfillment, customer support, and sending updates about daily gold/silver market rates if you have opted in.'
        },
        {
          heading: 'Data Security',
          text: 'We implement industry-standard security measures to protect your sensitive information during transactions and storage.'
        },
        {
          heading: 'Third-Party Sharing',
          text: 'We only share your information with trusted partners necessary for business operations, such as secure payment gateways and logistics providers.'
        }
      ]
    },
    terms: {
      title: 'Terms of Service',
      icon: <FileText className="h-6 w-6 text-brand-green" />,
      sections: [
        {
          heading: 'Product Authenticity',
          text: 'All gold jewellery sold at RPK is 100% BIS Hallmarked. We guarantee the purity specified (e.g., 22K, 18K) for every piece.'
        },
        {
          heading: 'Pricing Policy',
          text: 'Prices are calculated based on the daily retail market rates for gold and silver, plus applicable making charges and taxes.'
        },
        {
          heading: 'Returns & Exchange',
          text: 'Returns and buy-backs are processed at current market rates as per our transparent exchange policy. Original invoice is required for all transactions.'
        },
        {
          heading: 'Custom Orders',
          text: 'Bespoke and custom-designed jewellery orders require a non-refundable advance and may have specific delivery timelines.'
        }
      ]
    },
    cookies: {
      title: 'Cookie Policy',
      icon: <Cookie className="h-6 w-6 text-brand-green" />,
      sections: [
        {
          heading: 'What are Cookies?',
          text: 'Cookies are small text files stored on your device that help our website function efficiently and provide a better browsing experience.'
        },
        {
          heading: 'Essential Cookies',
          text: 'These are necessary for basic site features like maintaining your shopping session and account security.'
        },
        {
          heading: 'Performance Cookies',
          text: 'We use analytical cookies to understand how visitors interact with our website, helping us improve performance and design.'
        },
        {
          heading: 'Managing Cookies',
          text: 'You can choose to disable cookies through your browser settings, though some parts of the website may not function correctly as a result.'
        }
      ]
    }
  };

  const currentContent = type ? content[type] : null;

  if (!currentContent) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="p-6 border-b border-border bg-white flex flex-row items-center space-x-3 text-left">
          <div className="bg-brand-green/10 p-2 rounded-full">
            {currentContent.icon}
          </div>
          <DialogTitle className="text-2xl font-serif text-brand-green">
            {currentContent.title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-8 pb-4">
            {currentContent.sections.map((section, index) => (
              <div key={index} className="space-y-2">
                <h3 className="text-lg font-medium text-brand-green">{section.heading}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {section.text}
                </p>
              </div>
            ))}
            <div className="pt-4 text-xs text-muted-foreground border-t border-border italic">
              Last updated: May 2026. For further details, please contact our support team.
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
